import { zod4Resolver } from 'mantine-form-zod-resolver';
import type { z } from 'zod';
import type { MessageKey } from './da.js';

/**
 * A Mantine form resolver whose validation messages are translated.
 *
 * ## Why the schemas hold keys rather than text
 *
 * A Zod schema carrying user-facing messages has to be built from `t`, and a
 * schema built inside a component is a **new schema on every render** — which
 * makes a new resolver, which Mantine treats as a changed validator. The forms
 * here are `mode: 'uncontrolled'` and that is survivable, but it is waste on
 * every keystroke and it defeats the module-level constants these schemas were
 * written as.
 *
 * So the schemas stay module-level and carry message *keys*. This maps them
 * back through the catalogue at validation time, which is the one moment the
 * language is actually needed.
 *
 * ## Why unknown strings pass through
 *
 * Zod's own built-in messages — "Invalid input", "Too small" — arrive as plain
 * text, not keys. Translating only what looks like one of ours and leaving the
 * rest alone means a schema that has not been given a key still says something,
 * rather than rendering the raw string `Too small` as though it were a key that
 * failed to resolve.
 *
 * A key is recognised by its namespace prefix, which every key in the catalogue
 * has: `bank.`, `contacts.`, `att.`. The check is deliberately narrow — a
 * message that happens to contain a dot is not a key.
 */
export function translating(
  schema: z.ZodType,
  t: (key: MessageKey) => string,
  namespace: string,
): (values: Record<string, unknown>) => Record<string, string> {
  const resolve = zod4Resolver(schema);
  const prefix = `${namespace}.`;

  return (values) => {
    const errors = resolve(values) as Record<string, string>;
    return Object.fromEntries(
      Object.entries(errors).map(([field, message]) => [
        field,
        message.startsWith(prefix) ? t(message as MessageKey) : message,
      ]),
    );
  };
}
